import {
  ExternalSceneDto,
  ExternalSceneLoadState,
  ExternalSceneLocalStage,
  ExternalSceneProvider,
  ExternalSceneRowViewModel,
  ExternalSceneStatusKey,
  ExternalScenesCounters
} from './external-scenes.models';
import {
  estimateDownloadMinutes,
  formatArchiveSize,
  formatCloudCover,
  resolvePreviewUrl
} from './external-scenes-format.util';

const STAGES_DOWNLOADED_OR_LATER: ReadonlySet<ExternalSceneLocalStage> = new Set<ExternalSceneLocalStage>([
  'Downloaded',
  'Processing',
  'Processed',
  'ProcessingFailed'
]);

export const IDLE_LOAD_STATE: ExternalSceneLoadState = { status: 'idle', errorKey: null, assignmentId: null };

/** `Downloaded` and every later stage. */
export function isDownloadedOrLater(stage: ExternalSceneLocalStage | null | undefined): boolean {
  return !!stage && STAGES_DOWNLOADED_OR_LATER.has(stage);
}

/** Row status from the local stage and the client-side load state. */
export function resolveStatusKey(
  stage: ExternalSceneLocalStage | null | undefined,
  load: ExternalSceneLoadState
): ExternalSceneStatusKey {
  if (isDownloadedOrLater(stage)) {
    return 'EXTERNAL_SCENES.STATUS_DOWNLOADED';
  }
  if (stage === 'Downloading') {
    return 'EXTERNAL_SCENES.STATUS_DOWNLOADING';
  }
  if (load.status === 'pending') {
    return 'EXTERNAL_SCENES.STATUS_PENDING';
  }
  return 'EXTERNAL_SCENES.STATUS_NOT_LOADED';
}

/** Stable per-row identity: provider plus external id. */
export function sceneKey(provider: ExternalSceneProvider, externalId: string): string {
  return `${provider}:${externalId}`;
}

export function toRowViewModel(
  scene: ExternalSceneDto,
  load: ExternalSceneLoadState | undefined,
  apiBase: string
): ExternalSceneRowViewModel {
  const loadState = load ?? IDLE_LOAD_STATE;
  const stage = scene.localStage ?? null;
  const stageAllowsLoad = scene.canLoad && !isDownloadedOrLater(stage) && stage !== 'Downloading';
  return {
    id: sceneKey(scene.provider, scene.externalId),
    provider: scene.provider,
    externalId: scene.externalId,
    name: scene.name,
    providerLabelKey:
      scene.provider === 'Sentinel2' ? 'EXTERNAL_SCENES.PROVIDER_SENTINEL2' : 'EXTERNAL_SCENES.PROVIDER_LANDSAT',
    acquiredAt: scene.acquiredAt,
    cloudCoverLabel: formatCloudCover(scene.cloudCover),
    sizeLabel: formatArchiveSize(scene.archiveSizeBytes),
    etaMinutes: estimateDownloadMinutes(scene.archiveSizeBytes),
    previewUrl: resolvePreviewUrl(scene.previewUrl, apiBase),
    previewFormat: scene.previewUrl ? (scene.previewFormat ?? null) : null,
    statusKey: resolveStatusKey(stage, loadState),
    canLoad: stageAllowsLoad && loadState.status !== 'loading' && loadState.status !== 'pending',
    isLoading: loadState.status === 'loading',
    errorKey: loadState.status === 'error' ? loadState.errorKey : null,
    footprintGeoJson: scene.footprintGeoJson ?? null,
    centerLongitude: scene.centerLongitude ?? null,
    centerLatitude: scene.centerLatitude ?? null
  };
}

/** "In progress" = pending on the client or `Downloading` locally; "downloaded" = `Downloaded` or later. */
export function computeCounters(
  scenes: readonly ExternalSceneDto[],
  loadStates: ReadonlyMap<string, ExternalSceneLoadState>
): ExternalScenesCounters {
  let inProgress = 0;
  let downloaded = 0;
  for (const scene of scenes) {
    const stage = scene.localStage ?? null;
    if (isDownloadedOrLater(stage)) {
      downloaded++;
    } else if (
      stage === 'Downloading' ||
      loadStates.get(sceneKey(scene.provider, scene.externalId))?.status === 'pending'
    ) {
      inProgress++;
    }
  }
  return { inProgress, downloaded };
}
